import {
    absoluteUrl,
    qualityTarget,
    qualityUrl,
    rungUrl,
    startRung,
    viewportTarget,
} from '../../../lib/background/sets.js';
import {
    TIER_TIMEOUT_MS,
    connectionTier,
    recordSample,
    recordStall,
    subscribeTier,
    tierWidthCap,
} from '../../../lib/network/speed.js';

/** URLs already decoded this session. A background painted from here does not flash blank. */
const decoded = new Set();
/** Sharp files that ran out of time. The grid rung stays painted instead. */
const timedOut = new Set();
const listeners = new Set();

/**
 * Five lanes, in order: the open picture's grid-sized file, the open picture,
 * grid-sized files of the slides around it, the next slides one file at a time,
 * then grid-sized files for the rest of the directory. A lane does not start
 * until the lanes in front of it are idle, except that the open picture runs
 * beside its own grid file on a fast link. Focusing a new slide cancels
 * full-size work that is no longer on screen, so skimming does not stack
 * downloads; the slides around it keep their grid files coming, so a fast
 * cycle lands on a low-res picture the way the lightbox does over a grid
 * already on screen.
 */
const LANES = ['first', 'view', 'near', 'next', 'low'];
const LANE_LIMIT = { first: 4, view: 4, near: 3, low: 3 };
const waiting = { first: [], view: [], near: [], next: [], low: [] };
const active = new Map();
let viewBatch = new Set();
let firstBatch = new Set();
let viewBatchQueued = false;

/** The open picture's files and the lane each belongs to. Only these are retried. */
let focused = new Map();

/**
 * While slides change faster than this, the open picture's grid file still
 * loads at once but its full-size file and the slides ahead wait for the
 * viewer to settle.
 */
let settleMs = 200;
let settleTimer = null;
let lastFocusAt = Number.NEGATIVE_INFINITY;
let pendingView = new Set();
let pendingGroups = [];

const RETRY_DELAYS_MS = [500, 1500];
const retryTimers = new Set();

let loadImpl = defaultLoad;

const STALL_MS = 4000;
const PROGRESS_CHECK_MS = 1000;
const TIMEOUT = 'timeout';

function notify() {
    listeners.forEach((fn) => fn());
}

/** Held pictures repaint when a queued file finishes or the link speed changes. */
export function subscribeDecoded(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

function now() {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

async function readBody(res, onBytes) {
    if (!res.body?.getReader) {
        const blob = await res.blob();
        onBytes(blob.size);
        return blob;
    }
    const reader = res.body.getReader();
    const chunks = [];
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        onBytes(value.byteLength);
    }
    return new Blob(chunks, { type: res.headers.get('content-type') || '' });
}

/**
 * Give up when nothing has arrived for the tier's limit, when the download
 * is on course to take more than twice that, or after three times it in total.
 * A fast link still finishes a large file that is making steady progress.
 */
function shouldGiveUp({ elapsed, idle, bytes, total, limit }) {
    if (idle > limit) return true;
    if (elapsed > limit * 3) return true;
    if (elapsed > limit && total > 0 && bytes > 0) {
        return (elapsed * total) / bytes > limit * 2;
    }
    return false;
}

/** Several downloads at once share the link, so a slow one says little about its speed. */
function measuringAlone() {
    return active.size <= 1;
}

async function defaultLoad(url, { signal, priority, patient = false } = {}) {
    if (typeof fetch !== 'function' || signal?.aborted) return false;
    const controller = new AbortController();
    const forward = () => controller.abort();
    signal?.addEventListener('abort', forward, { once: true });
    const limit = TIER_TIMEOUT_MS[connectionTier()] || TIER_TIMEOUT_MS.fast;
    const started = now();
    let lastByte = started;
    let bytes = 0;
    let total = 0;
    let stalled = false;
    let gaveUp = false;
    const watch = setInterval(() => {
        const at = now();
        const elapsed = at - started;
        if (!stalled && elapsed >= STALL_MS && measuringAlone()) {
            stalled = true;
            recordStall(elapsed, bytes);
        }
        if (!patient && shouldGiveUp({ elapsed, idle: at - lastByte, bytes, total, limit })) {
            gaveUp = true;
            controller.abort();
        }
    }, PROGRESS_CHECK_MS);
    try {
        const res = await fetch(url, { signal: controller.signal, priority, credentials: 'same-origin' });
        if (!res.ok) return false;
        total = Number(res.headers.get('content-length')) || 0;
        const blob = await readBody(res, (count) => {
            bytes += count;
            lastByte = now();
        });
        if (controller.signal.aborted) return gaveUp ? TIMEOUT : false;
        recordSample(blob.size || bytes, now() - started);
        if (typeof createImageBitmap === 'function') {
            const bitmap = await createImageBitmap(blob);
            bitmap.close?.();
        }
        return true;
    } catch {
        if (gaveUp) {
            if (measuringAlone()) recordStall(now() - started, bytes);
            return TIMEOUT;
        }
        return false;
    } finally {
        clearInterval(watch);
        signal?.removeEventListener('abort', forward);
    }
}

/**
 * A loader resolves true when decoded, false on failure or cancel, and
 * 'timeout' when the file was too slow; that file is not asked for again.
 */
function loadUrl(url, options) {
    if (!url) return Promise.resolve(false);
    if (decoded.has(url)) return Promise.resolve(true);
    return Promise.resolve(loadImpl(url, options)).then((ok) => {
        if (ok === TIMEOUT) {
            timedOut.add(url);
            notify();
            return false;
        }
        if (ok && !options?.signal?.aborted) {
            decoded.add(url);
            notify();
            return true;
        }
        return false;
    }).catch(() => false);
}

/** Re-export so React code repaints when the link tier changes. */
export function subscribeNetworkTier(fn) {
    return subscribeTier(fn);
}

let lastTier = null;
subscribeTier((tier) => {
    const previous = lastTier;
    lastTier = tier;
    if (tier === 'fast' && previous && previous !== 'fast') timedOut.clear();
    if (tier === 'slow') {
        waiting.next.forEach((job) => job.resolve(false));
        waiting.next = [];
    }
    notify();
});

function laneCount(lane) {
    let count = 0;
    for (const job of active.values()) {
        if (job.lane === lane) count += 1;
    }
    return count;
}

function listed(lane, url) {
    return waiting[lane].some((job) => job.url === url);
}

function laneBusy(lane) {
    return waiting[lane].length > 0 || laneCount(lane) > 0;
}

function lowLimit() {
    return connectionTier() === 'slow' ? 1 : LANE_LIMIT.low;
}

function nearLimit() {
    return connectionTier() === 'slow' ? 1 : LANE_LIMIT.near;
}

function activeNextGroup() {
    for (const job of active.values()) {
        if (job.lane === 'next') return job.group;
    }
    return null;
}

function rank(lane) {
    return LANES.indexOf(lane);
}

function settle(resolvers, ok) {
    resolvers.forEach((resolve) => resolve(ok));
}

/**
 * Wait on a download that is already running, and move it to the more urgent
 * lane, so clearing the slides ahead cannot cancel the picture now open.
 */
function join(running, lane, resolve) {
    if (resolve) running.resolvers.push(resolve);
    if (rank(lane) < rank(running.lane)) running.lane = lane;
}

function promote(urls, lane) {
    urls.forEach((url) => {
        const running = active.get(url);
        if (running) join(running, lane);
    });
}

function shouldRetry(running, url) {
    if (running.lane !== 'first' && running.lane !== 'view') return false;
    if (running.attempt >= RETRY_DELAYS_MS.length) return false;
    if (running.controller.signal.aborted || timedOut.has(url)) return false;
    return focused.has(url);
}

function scheduleRetry(url, running) {
    const timer = setTimeout(() => {
        retryTimers.delete(timer);
        if (decoded.has(url)) return settle(running.resolvers, true);
        const lane = focused.get(url);
        if (!lane || active.has(url) || listed(lane, url)) return settle(running.resolvers, false);
        waiting[lane].unshift({
            url,
            resolve: (ok) => settle(running.resolvers, ok),
            attempt: running.attempt + 1,
        });
        return pump();
    }, RETRY_DELAYS_MS[running.attempt]);
    retryTimers.add(timer);
}

function startJob(lane, job) {
    if (decoded.has(job.url)) {
        job.resolve(true);
        return;
    }
    const existing = active.get(job.url);
    if (existing) {
        join(existing, lane, job.resolve);
        return;
    }
    const controller = new AbortController();
    const running = {
        controller,
        lane,
        group: job.group,
        resolvers: [job.resolve],
        attempt: job.attempt || 0,
        dropped: false,
    };
    active.set(job.url, running);
    loadUrl(job.url, {
        priority: lane === 'low' ? 'low' : 'high',
        signal: controller.signal,
        // The open picture's grid file is what keeps the frame from being blank.
        patient: lane === 'first',
    }).then((ok) => {
        if (running.dropped) return;
        if (active.get(job.url) === running) active.delete(job.url);
        if (!ok && shouldRetry(running, job.url)) {
            scheduleRetry(job.url, running);
        } else {
            settle(running.resolvers, ok);
        }
        pump();
    });
}

function pump() {
    while (laneCount('first') < LANE_LIMIT.first && waiting.first.length) {
        startJob('first', waiting.first.shift());
    }
    // On a slower link the grid file gets the whole pipe first, so something
    // is on screen before the sharp file starts competing for bandwidth.
    const holdView = connectionTier() !== 'fast' && laneBusy('first');
    if (!holdView) {
        while (laneCount('view') < LANE_LIMIT.view && waiting.view.length) {
            startJob('view', waiting.view.shift());
        }
    }
    if (laneBusy('first') || laneBusy('view')) return;

    while (laneCount('near') < nearLimit() && waiting.near.length) {
        startJob('near', waiting.near.shift());
    }
    if (laneBusy('near')) return;

    const started = activeNextGroup();
    const group = started != null ? started : waiting.next[0]?.group;
    if (group != null) {
        let guard = 0;
        while (guard < 8) {
            const index = waiting.next.findIndex((job) => job.group === group);
            if (index < 0) break;
            const [job] = waiting.next.splice(index, 1);
            startJob('next', job);
            guard += 1;
        }
        if (laneCount('next') > 0 || waiting.next.some((job) => job.group === group)) return;
    }

    while (laneCount('low') < lowLimit() && waiting.low.length) {
        startJob('low', waiting.low.shift());
    }
}

function abortWhere(match, requeueLane) {
    for (const [url, job] of active) {
        if (!match(job, url)) continue;
        job.dropped = true;
        active.delete(url);
        job.controller.abort();
        if (requeueLane && !decoded.has(url)) {
            waiting[requeueLane].unshift({ url, resolve: (ok) => settle(job.resolvers, ok) });
        } else {
            settle(job.resolvers, false);
        }
    }
}

function pauseLow() {
    abortWhere((job) => job.lane === 'low', 'low');
}

function enqueue(lane, url) {
    if (!url) return Promise.resolve(false);
    if (decoded.has(url)) return Promise.resolve(true);
    const running = active.get(url);
    if (running) return new Promise((resolve) => join(running, lane, resolve));
    const queued = waiting[lane].find((job) => job.url === url);
    if (queued) {
        return new Promise((resolve) => {
            const before = queued.resolve;
            queued.resolve = (ok) => {
                before(ok);
                resolve(ok);
            };
        });
    }
    return new Promise((resolve) => {
        waiting[lane].push({ url, resolve });
        pump();
    });
}

/** Queue a grid-sized file. It waits while a story's full-size pictures load. */
export function enqueueLowUrl(url) {
    return enqueue('low', url);
}

function replaceLane(lane, urls, keepActive) {
    const keep = new Set(urls);
    abortWhere((job, url) => job.lane === lane && !(keepActive && keep.has(url)));
    waiting[lane].forEach((job) => {
        if (!keep.has(job.url)) job.resolve(false);
    });
    waiting[lane] = waiting[lane].filter((job) => keep.has(job.url));
    urls.forEach((url) => enqueue(lane, url));
}

function asGroups(ahead) {
    if (!Array.isArray(ahead) || ahead.length === 0) return [];
    if (Array.isArray(ahead[0])) return ahead;
    return ahead.filter(Boolean).map((url) => [url]);
}

/**
 * Full-size files for the open picture start immediately, including both pages
 * of a spread. Downloads for slides the viewer has already left are cancelled.
 * Each ahead entry is one slide: both of its pictures start together, and the
 * following slide waits until that slide has finished. `firstUrls` are the
 * open picture's grid files; they go before everything so the frame is never
 * blank while the sharp file downloads. `nearUrls` are grid files of the slides
 * around it, nearest first; they keep loading while slides change fast and come
 * before the full-size slides ahead. On a slow link nothing loads ahead at full size.
 */
export function focusPictures(viewUrls, soonUrls = [], firstUrls = [], nearUrls = []) {
    const view = [...new Set((viewUrls || []).filter(Boolean))];
    const first = [...new Set((firstUrls || []).filter((url) => url && !view.includes(url)))];
    const near = [...new Set((nearUrls || []).filter((url) => (
        url && !view.includes(url) && !first.includes(url) && !decoded.has(url)
    )))];
    const groups = connectionTier() === 'slow' ? [] : asGroups(soonUrls);
    focused = new Map([
        ...first.map((url) => [url, 'first']),
        ...view.map((url) => [url, 'view']),
    ]);
    // A file already downloading as a slide ahead is now the open picture.
    promote(first, 'first');
    promote(view, 'view');
    promote(near, 'near');
    pauseLow();
    replaceLane('first', first, true);
    // Full-size work for slides already left stops now, even while skimming.
    abortWhere((job, url) => job.lane === 'view' && !view.includes(url));
    waiting.view.forEach((job) => {
        if (!view.includes(job.url)) job.resolve(false);
    });
    waiting.view = waiting.view.filter((job) => view.includes(job.url));
    abortWhere((job) => job.lane === 'next');
    waiting.next.forEach((job) => job.resolve(false));
    waiting.next = [];

    const at = now();
    const skimming = at - lastFocusAt < settleMs;
    lastFocusAt = at;
    pendingView = new Set(view);
    pendingGroups = groups;
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = null;
    if (skimming) {
        settleTimer = setTimeout(flushPendingSharp, settleMs);
    } else {
        flushPendingSharp();
    }
    // After the open picture's full-size file is queued, so it still goes first once settled.
    replaceLane('near', near, true);
    pump();
}

function flushPendingSharp() {
    settleTimer = null;
    const view = [...pendingView];
    const groups = pendingGroups;
    pendingView = new Set();
    pendingGroups = [];
    view.forEach((url) => enqueue('view', url));
    groups.forEach((urls, group) => {
        [...new Set(urls.filter(Boolean))].forEach((url) => {
            if (view.includes(url) || decoded.has(url)) return;
            waiting.next.push({ url, group, resolve: () => {} });
        });
    });
    pauseLow();
    pump();
}

/** Add the open picture without cancelling the other page of its spread. */
export function focusView(viewUrls, firstUrls = []) {
    (firstUrls || []).forEach((url) => {
        if (url && !focused.has(url)) focused.set(url, 'first');
    });
    (viewUrls || []).forEach((url) => {
        if (url) focused.set(url, 'view');
    });
    promote(firstUrls || [], 'first');
    promote(viewUrls || [], 'view');
    pauseLow();
    (firstUrls || []).forEach((url) => enqueue('first', url));
    if (settleTimer) {
        (viewUrls || []).forEach((url) => {
            if (url) pendingView.add(url);
        });
    } else {
        (viewUrls || []).forEach((url) => enqueue('view', url));
    }
    pump();
}

/** Ask again for the open picture's files that are not on screen yet. */
function retryFocused() {
    focused.forEach((lane, url) => {
        if (decoded.has(url) || timedOut.has(url) || active.has(url) || listed(lane, url)) return;
        if (lane === 'view' && settleTimer) return;
        enqueue(lane, url);
    });
    pump();
}

if (typeof window !== 'undefined') {
    window.addEventListener('online', retryFocused);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') retryFocused();
    });
}

export function prioritizeFile(file, target = viewerTargetWidth()) {
    if (typeof window === 'undefined' || !file) return;
    const { sharp, low } = pictureUrls(file, target);
    const url = sharp || low;
    if (!url || decoded.has(url)) return;
    viewBatch.add(url);
    if (sharp && low && !decoded.has(low)) firstBatch.add(low);
    if (viewBatchQueued) return;
    viewBatchQueued = true;
    queueMicrotask(() => {
        viewBatchQueued = false;
        const urls = [...viewBatch];
        const firsts = [...firstBatch];
        viewBatch = new Set();
        firstBatch = new Set();
        if (!urls.length) return;
        focusView(urls, firsts);
    });
}

export function prefetchLowFiles(files, target = viewerTargetWidth()) {
    (files || []).forEach((file) => {
        const { low } = pictureUrls(file, target);
        if (low) enqueueLowUrl(low);
    });
}

/** Test seam. The loader resolves true when the file should count as decoded. */
export function setPictureLoader(fn) {
    loadImpl = fn || defaultLoad;
}

/** Test seam. How long slide changes must pause before full-size files load. */
export function setFocusSettleMs(ms) {
    settleMs = Number.isFinite(ms) ? Math.max(0, ms) : 200;
}

export function resetPictureNetwork() {
    viewBatch = new Set();
    firstBatch = new Set();
    viewBatchQueued = false;
    focused = new Map();
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = null;
    lastFocusAt = Number.NEGATIVE_INFINITY;
    pendingView = new Set();
    pendingGroups = [];
    settleMs = 200;
    retryTimers.forEach((timer) => clearTimeout(timer));
    retryTimers.clear();
    LANES.forEach((lane) => {
        waiting[lane].splice(0, waiting[lane].length);
    });
    for (const job of active.values()) job.controller.abort();
    active.clear();
    decoded.clear();
    timedOut.clear();
    loadImpl = defaultLoad;
}

/**
 * Width the open picture should reach. A fast link gets one step above the
 * screen match; a slower one gets the plain match, capped for the tier.
 */
export function viewerTargetWidth() {
    const tier = connectionTier();
    const cap = tierWidthCap(tier);
    if (typeof window === 'undefined') return Math.min(1920, cap);
    const width = window.innerWidth;
    const ratio = window.devicePixelRatio || 1;
    const base = tier === 'fast' ? qualityTarget(width, ratio) : viewportTarget(width, ratio);
    return Math.min(base, cap);
}

/**
 * Low rung (grid) and a file sharper than the direct screen match. A slow
 * link never asks for the camera original, and a sharp file that already ran
 * out of time is left out so the grid file stays on screen.
 */
export function pictureUrls(file, target = viewerTargetWidth()) {
    const tier = connectionTier();
    const low = absoluteUrl(rungUrl(file, startRung()));
    const request = Math.min(target, tierWidthCap(tier));
    const sharp = absoluteUrl(qualityUrl(file, request, { allowOriginal: tier === 'fast' }));
    return {
        low,
        sharp: sharp && sharp !== low && !timedOut.has(sharp) ? sharp : '',
    };
}

/**
 * What can be painted now. Null until the low or sharp file has decoded,
 * so a caller can keep the previous picture instead of clearing the frame.
 * @param {Set<string>|function} [known]
 */
export function readyPaint(file, target = viewerTargetWidth(), known = decoded) {
    const has = typeof known === 'function' ? known : (url) => known.has(url);
    const { low, sharp } = pictureUrls(file, target);
    if (!low && !sharp) return null;
    const sharpReady = Boolean(sharp && has(sharp));
    const lowReady = Boolean(low && has(low));
    if (!sharpReady && !lowReady) return null;
    return {
        key: low || sharp,
        low: lowReady ? low : '',
        sharp: sharpReady ? sharp : (lowReady ? '' : sharp),
    };
}

/** Queue the grid rung. A full-size request pauses this queue. */
export function warmLow(file, target = viewerTargetWidth()) {
    const { low } = pictureUrls(file, target);
    if (!low) return Promise.resolve(null);
    return enqueueLowUrl(low).then(() => readyPaint(file, target));
}

/**
 * The open picture's sharp file runs immediately. Anything else waits behind it.
 * On a slow link a picture that is not open only warms its grid file.
 */
export function warmPicture(file, target = viewerTargetWidth(), priority = 'low') {
    const urls = pictureUrls(file, target);
    const { low } = urls;
    const sharp = priority !== 'high' && connectionTier() === 'slow' ? '' : urls.sharp;
    const paint = () => readyPaint(file, target);
    if (!sharp && priority !== 'high') {
        const lowOnly = low ? enqueueLowUrl(low).then(paint) : Promise.resolve(null);
        return { low: lowOnly, sharp: lowOnly };
    }
    const sharpUrl = sharp || low;
    const sharpJob = (sharpUrl
        ? (priority === 'high' ? enqueue('view', sharpUrl) : enqueue('next', sharpUrl))
        : Promise.resolve(false)
    ).then(paint);
    const lowJob = (low && low !== sharpUrl)
        ? enqueueLowUrl(low).then(paint)
        : Promise.resolve(null);
    return { low: lowJob, sharp: sharpJob };
}

/** Decode both rungs. Resolves once the sharp file has settled. */
export function ensureDecoded(file, target = viewerTargetWidth(), priority = 'low') {
    const warm = warmPicture(file, target, priority);
    return warm.sharp.then(() => readyPaint(file, target));
}

export function slideFiles(slide) {
    if (!slide) return [];
    if (Array.isArray(slide.images)) return slide.images.map((image) => image.file).filter(Boolean);
    if (slide.file) return [slide.file];
    return [];
}

/** True when every picture in the slide has a decoded rung to paint. */
export function slideLowReady(slide, target = viewerTargetWidth()) {
    const files = slideFiles(slide);
    if (!files.length) return true;
    return files.every((file) => {
        const paint = readyPaint(file, target);
        return Boolean(paint && (paint.low || paint.sharp));
    });
}
